/**
 * Persistence for the signed-in user's locale, sourced from the
 * `ssel/users/me` response (`user.locale`). The value is saved to
 * localStorage so the next page load can boot i18next in the right
 * language before the API responds.
 *
 * This module must stay free of i18n imports: `src/locales/index.ts` reads
 * the saved value during init, and importing the i18n instance from here
 * would create a cycle. The apply-side lives in `src/locales/index.ts`
 * (`applyUserLocale`).
 */

export const USER_LOCALE_STORAGE_KEY = 'ft-user-locale';

export function getSavedUserLocale(): string | undefined {
  try {
    return window.localStorage.getItem(USER_LOCALE_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function saveUserLocale(locale: string): void {
  try {
    window.localStorage.setItem(USER_LOCALE_STORAGE_KEY, locale);
  } catch {
    // localStorage unavailable (private mode etc.) — the locale still
    // applies for this session, it just won't persist across loads.
  }
}

/**
 * Dev/test locale override — lets anyone preview the dashboard in another
 * language (or i18next's built-in `cimode`, which renders raw keys so
 * unmigrated hardcoded strings stand out) without touching their real user
 * locale. Wins over the users/me locale everywhere.
 *
 * Two ways in:
 *   - URL param: `?ft-locale=fr-fr` (persists in localStorage across
 *     reloads until cleared with `?ft-locale=off`)
 *   - console: `ftLocale.set('fr-fr')` / `ftLocale.clear()` (see
 *     src/locales/index.ts)
 */
export const LOCALE_OVERRIDE_STORAGE_KEY = 'ft-locale-override';
export const LOCALE_OVERRIDE_QUERY_PARAM = 'ft-locale';
const LOCALE_OVERRIDE_OFF = 'off';

export function saveLocaleOverride(locale: string | undefined): void {
  try {
    if (locale) {
      window.localStorage.setItem(LOCALE_OVERRIDE_STORAGE_KEY, locale);
    } else {
      window.localStorage.removeItem(LOCALE_OVERRIDE_STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable — the override won't survive a reload.
  }
}

export function getLocaleOverride(): string | undefined {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(
      LOCALE_OVERRIDE_QUERY_PARAM,
    );
    if (fromUrl !== null) {
      const normalized =
        fromUrl === LOCALE_OVERRIDE_OFF || fromUrl === ''
          ? undefined
          : fromUrl.toLowerCase();
      saveLocaleOverride(normalized);
      return normalized;
    }
    return (
      window.localStorage.getItem(LOCALE_OVERRIDE_STORAGE_KEY) ?? undefined
    );
  } catch {
    return undefined;
  }
}
